import React, { memo, useMemo } from 'react';

import { Box, Badge as UCLBadge } from '@rbilabs/universal-components';
import { Image } from 'expo-image';
import { Tabs } from 'expo-router';
import { InteractionManager, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { StandardMobileHeader } from 'components/app-header/mobile-header';
import { useAppTakeover } from 'components/app-takeover/use-app-takeover';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logRBIEvent,
} from 'state/amplitude';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { primitive } from 'styles/constants/primitives';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { isWeb } from 'utils/environment';
import { startTrace } from 'utils/performance/trace';
import { TraceName } from 'utils/performance/types';

export const unstable_settings = {
  initialRouteName: 'index',
};

function TabsLayout() {
  const isDesktopWeb = useIsDesktopWebBreakpoint();

  const screenOptions = useMemo(
    () => ({
      freezeOnBlur: true,
      lazy: true,
      header: () => <StandardMobileHeader />,
      tabBarButton: NativeTouchFeedbackTabBarIcon,
      tabBarStyle: tabBarStyles.tabBarStyle,
      tabBarLabelPosition: 'below-icon' as const,
      sceneStyle: { backgroundColor: primitive.bk.$mayoEggWhite },
    }),
    []
  );

  const navHomeText = t('navHome');
  const navMenuText = t('navMenu');
  const navMyCodeText = t('navMyCode');
  const navOffersText = t('navOffers');
  const navRewardsText = t('navRewards');

  return (
    <Tabs screenOptions={screenOptions}>
      <Tabs.Screen
        name="index"
        {...ANALYTICS_HOME}
        options={useMemo(
          () => ({
            header: () => <StandardMobileHeader hideTsm={!isDesktopWeb} />,
            title: navHomeText,
            tabBarButtonTestID: getTestId('Home'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel text={navHomeText} focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="home" focused={focused} altId="navHome" />
            ),
          }),
          [isDesktopWeb, navHomeText]
        )}
      />
      <Tabs.Screen
        name="menu"
        {...ANALYTICS_MENU}
        options={useMemo(
          () => ({
            title: navMenuText,
            tabBarButtonTestID: getTestId('Menu'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel text={navMenuText} focused={focused} />
            ),
            headerShown: false,
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="menu" focused={focused} altId="navMenu" />
            ),
          }),
          [navMenuText]
        )}
      />
      <Tabs.Screen
        name="my-code"
        {...ANALYTICS_MY_CODE}
        options={useMemo(
          () => ({
            title: navMyCodeText,
            tabBarButtonTestID: getTestId('My-Code'),
            tabBarItemStyle: {
              display: isDesktopWeb ? 'none' : 'flex',
            },
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel text={navMyCodeText} focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon
                name="myCode"
                focused={focused}
                BadgeComponent={MyCodeBadge}
                altId="navMyCode"
              />
            ),
          }),
          [isDesktopWeb, navMyCodeText]
        )}
      />
      <Tabs.Screen
        name="offers"
        {...ANALYTICS_OFFERS}
        options={useMemo(
          () => ({
            title: navOffersText,
            tabBarButtonTestID: getTestId('Offers'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel text={navOffersText} focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="offers" focused={focused} altId="navOffers" />
            ),
            headerShown: false,
          }),
          [navOffersText]
        )}
      />
      <Tabs.Screen
        name="rewards"
        {...ANALYTICS_REWARDS}
        options={useMemo(
          () => ({
            title: navRewardsText,
            tabBarButtonTestID: getTestId('Rewards'),
            tabBarLabel: ({ focused }: { focused: boolean }) => (
              <TabLabel text={navRewardsText} focused={focused} />
            ),
            tabBarIcon: ({ focused }: { focused: boolean }) => (
              <TabBarIcon name="rewards" focused={focused} altId="navRewards" />
            ),
          }),
          [navRewardsText]
        )}
      />
    </Tabs>
  );
}

export default memo(TabsLayout);

const getTestId = (name: string) => `nav-with-picture-icon-${name}`;

const createAnalyticsEvent = (name: string, route: string, traceName?: TraceName) => ({
  listeners: {
    tabPress: () => {
      if (traceName) {
        startTrace(traceName, { tabPress: 'true' });
      }
      InteractionManager.runAfterInteractions(() => {
        logRBIEvent({
          name: CustomEventNames.CLICK_EVENT,
          type: EventTypes.Other,
          attributes: {
            text: name,
            component: ClickEventComponentNames.NAV_BAR,
            componentId: route,
          },
        });
      });
    },
  },
});

const ANALYTICS_HOME = createAnalyticsEvent('Home', '/', TraceName.HOME_LOAD);
const ANALYTICS_MENU = createAnalyticsEvent('Menu', '/menu', TraceName.MENU_LOAD);
const ANALYTICS_MY_CODE = createAnalyticsEvent('My Code', '/my-code', TraceName.MY_CODE_LOAD);
const ANALYTICS_OFFERS = createAnalyticsEvent('Offers', '/offers', TraceName.OFFERS_LOAD);
const ANALYTICS_REWARDS = createAnalyticsEvent('Rewards', '/rewards', TraceName.REWARDS_LOAD);

// this doesn't work on web
const NativeTouchFeedbackTabBarIcon = !isWeb
  ? ({ key, ...props }: any) => <TouchableOpacity key={key} activeOpacity={0.3} {...props} />
  : undefined;

const TAB_ICONS = {
  home: {
    base: require('../../assets/images/nav/home.svg'),
    active: require('../../assets/images/nav/home-active.svg'),
  },
  menu: {
    base: require('../../assets/images/nav/menu.svg'),
    active: require('../../assets/images/nav/menu-active.svg'),
  },
  myCode: {
    base: require('../../assets/images/nav/my-code.svg'),
    active: require('../../assets/images/nav/my-code-active.svg'),
  },
  offers: {
    base: require('../../assets/images/nav/offers.svg'),
    active: require('../../assets/images/nav/offers-active.svg'),
  },
  rewards: {
    base: require('../../assets/images/nav/rewards.svg'),
    active: require('../../assets/images/nav/rewards-active.svg'),
  },
};

type TabBarIconProps = {
  name: string;
  focused: boolean;
  altId: TranslationKey;
  BadgeComponent?: React.ComponentType | undefined;
};

const TabBarIcon: React.FC<TabBarIconProps> = memo(({ name, focused, altId, BadgeComponent }) => {
  const alt = t(altId);
  const appTakeover = useAppTakeover();
  const appTakeoverMenuIcon = name === 'menu' ? appTakeover?.menuIcon : undefined;
  return (
    <View style={tabBarStyles.iconWrapper}>
      <View
        style={[tabBarStyles.activeIndicatorContainer, focused && tabBarStyles.activeIndicator]}
      />
      <Image
        accessibilityLabel={alt}
        source={appTakeoverMenuIcon || (focused ? TAB_ICONS[name].active : TAB_ICONS[name].base)}
        style={tabBarStyles.iconStyles}
        {...hiddenAccessibilityPlatformProps}
      />
      {BadgeComponent ? <BadgeComponent /> : null}
    </View>
  );
});

const TabLabel: React.FC<{ text: string; focused: boolean }> = memo(({ text, focused }) => {
  return (
    <Text style={focused ? tabBarStyles.focusedLabelStyle : tabBarStyles.unfocusedLabelStyle}>
      {text}
    </Text>
  );
});

const MyCodeBadge = memo(() => {
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();
  const count = inRestaurantRedemptionCart.reduce(
    (acc, currentValue) => acc + currentValue.quantity,
    0
  );

  if (!count) {
    return null;
  }

  return (
    <BadgeOuter>
      <BadgeInner variant="number-solid">{count.toString()}</BadgeInner>
    </BadgeOuter>
  );
});

const BadgeOuter = Box.withConfig({
  position: 'absolute',
  zIndex: 1,
  top: -5,
  right: 5,
});

const BadgeInner = UCLBadge.withConfig({
  bgColor: primitive.bk.$meltyYellow,
  _text: {
    fontFamily: 'heading',
    fontSize: 'xs',
    color: primitive.bk.$bbqBrown,
  },
  width: '$5',
  height: '$5',
  style: {
    minWidth: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
});

export const tabBarStyles = StyleSheet.create({
  tabBarStyle: {
    backgroundColor: primitive.bk.$mayoEggWhite,
    borderTopColor: '#FFFFFF',
    borderTopWidth: 1,
  },
  iconWrapper: {
    alignItems: 'center',
  },
  activeIndicatorContainer: {
    height: 3,
    top: -7,
  },
  activeIndicator: {
    backgroundColor: primitive.bk.$bbqBrown,
    width: 70,
  },
  iconStyles: { height: 22, width: 22 },
  focusedLabelStyle: { fontFamily: 'Flame-Regular', color: primitive.bk.$bbqBrown, fontSize: 12 },
  unfocusedLabelStyle: { fontFamily: 'Flame-RegularSans', color: '#82685B', fontSize: 12 },
});
